import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AppModal } from '../../shared/components/AppModal'
import { useAppConfirm } from '../../shared/components/AppConfirmDialog'
import { useLocale } from '../../shared/i18n/LocaleContext'
import { SortableHeaders, useUrlSortedRows } from '../../shared/components/SortableTableHeader'
import { getPreferredPageSize, recordListPageSizes, rememberPageSize,
  type RecordListPageSize } from '../../shared/pagination/pageSizePreference'
import { cancelIntegrationExecution, getIntegrationExecution,
  getIntegrationExecutions, getRunningIntegrationExecutions, integratorRoute } from './integratorApi'
import type { IntegrationExecution, IntegrationExecutionDetail,
  IntegrationExecutionPage } from './types'

const prettyResponseJson = (value:string) => {
  try {
    const pages = JSON.parse(value) as unknown
    if (Array.isArray(pages) && pages.every(item => typeof item === 'string')) {
      const parsed = pages.map(item => JSON.parse(item as string) as unknown)
      return JSON.stringify(parsed.length === 1 ? parsed[0] : parsed, null, 2)
    }
    return JSON.stringify(pages, null, 2)
  } catch { return value }
}

const integrationErrorReason = (errorCode:string, errorDetail:string,
  t:(key:string)=>string) => errorCode.startsWith('integrator.validation.')
    ? t(errorCode)
    : errorDetail || '—'

const readableDuration = (milliseconds:number, formatNumber:(value:number)=>string,
  t:(key:string)=>string) => {
  if (milliseconds < 1000) return t('integrator.durationMilliseconds')
    .replace('{milliseconds}', formatNumber(milliseconds))
  const totalSeconds = Math.floor(milliseconds / 1000)
  if (totalSeconds < 60) return t('integrator.durationSeconds')
    .replace('{seconds}', formatNumber(totalSeconds))
  return t('integrator.durationMinutesSeconds')
    .replace('{minutes}', formatNumber(Math.floor(totalSeconds / 60)))
    .replace('{seconds}', formatNumber(totalSeconds % 60))
}

export function IntegrationConnectionsPage() {
  const { configurationId = '', companyId = '', integrationId = '' } = useParams()
  const context = useMemo(() => ({ configurationId, companyId }),
    [configurationId, companyId])
  const studioRoute = integratorRoute(context)
  const globalRunning = !integrationId
  const { t, formatDateTime, formatNumber, locale } = useLocale()
  const { confirm, confirmationDialog } = useAppConfirm()
  const [result, setResult] = useState<IntegrationExecutionPage|null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<RecordListPageSize>(() => getPreferredPageSize(undefined, 10))
  const [selected, setSelected] = useState<IntegrationExecution|null>(null)
  const [detail, setDetail] = useState<IntegrationExecutionDetail|null>(null)
  const [detailView, setDetailView] = useState<'json'|'records'|null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string|null>(null)

  const load = useCallback(async (showBusy = true) => {
    if (showBusy) setBusy(true)
    try { setResult(globalRunning
      ? await getRunningIntegrationExecutions(context, page, pageSize)
      : await getIntegrationExecutions(context, integrationId, page, pageSize)); setError(null) }
    catch (reason) { setError(reason instanceof Error ? reason.message :
      'integrator.executionDetailError') }
    finally { if (showBusy) setBusy(false) }
  }, [context, globalRunning, integrationId, page, pageSize])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    if (!result?.items.some(item => item.status === 'Pending' ||
        item.status === 'Running')) return
    const timer = window.setInterval(() => void load(false), 5000)
    return () => window.clearInterval(timer)
  }, [load, result?.items])
  useEffect(() => {
    if (!selected || !result) return
    const refreshed = result.items.find(item => item.id === selected.id)
    if (refreshed) setSelected(refreshed)
  }, [result, selected?.id])
  const totalPages = useMemo(() => Math.max(1,
    Math.ceil((result?.totalCount ?? 0) / pageSize)), [pageSize, result?.totalCount])

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const timingRows = selected ? [
    ['integrator.timingPreparation', selected.preparationMilliseconds],
    ['integrator.timingExternalRequest', selected.externalRequestMilliseconds],
    ['integrator.timingResponseParsing', selected.responseParsingMilliseconds],
    ['integrator.timingMapping', selected.mappingMilliseconds],
    ['integrator.timingLookup', selected.lookupMilliseconds],
    ['integrator.timingApply', selected.applyMilliseconds],
    ['integrator.timingPersistence', selected.persistenceMilliseconds],
  ] as const : []
  const measuredMilliseconds = timingRows.reduce((sum, row) => sum + row[1], 0)
  const otherMilliseconds = Math.max(0,
    (selected?.elapsedMilliseconds ?? 0) - measuredMilliseconds)

  const openDetail = (item:IntegrationExecution) => {
    setSelected(item); setDetail(null); setDetailView(null)
  }
  const showDetail = async (view:'json'|'records') => {
    if (!selected) return
    setBusy(true); setError(null)
    try {
      const loaded = detail ?? await getIntegrationExecution(context, selected.id)
      setDetail(loaded); setDetailView(view)
    } catch (reason) { setError(reason instanceof Error ? reason.message :
      'integrator.executionDetailError') }
    finally { setBusy(false) }
  }
  const cancelExecution = async (item:IntegrationExecution) => {
    if (!await confirm(t('integrator.cancelExecutionConfirm'),
      t('integrator.cancelExecution'))) return
    setBusy(true); setError(null)
    try { await cancelIntegrationExecution(context, item.id); await load(false) }
    catch (reason) { setError(reason instanceof Error ? reason.message :
      'integrator.executionCannotCancel') }
    finally { setBusy(false) }
  }
  const tableSort=useUrlSortedRows(result?.items??[],{name:x=>x.integrationName,kind:x=>x.kind,status:x=>x.status,
    started:x=>x.startedAtUtc??x.createdAtUtc,pages:x=>x.pagesRead,records:x=>x.recordsRead},'started','desc',locale)

  return <main className="page-content">
    <header className="welcome administration-heading">
      <div><span className="eyebrow">{t('integrator.eyebrow')}</span>
        <h1>{t('integrator.executionHistory')}</h1>
        <p>{result ? `${result.companyName}${globalRunning ? '' : ` · ${result.integrationName}`}` :
          t('integrator.executionHistoryHelp')}</p></div>
    </header>
    <nav className="apl-context-navigation"
      aria-label={t('recordNavigation.backToList')}>
      <Link className="secondary-button" to={studioRoute}>
        ← {t('integrator.backToStudio')}
      </Link>
    </nav>
    {error && <p className="form-error">{t(error as Parameters<typeof t>[0])}</p>}
    <section className="data-panel administration-panel">
      <div className="section-heading integration-history-heading"><div>
        <h2>{globalRunning ? t('integrator.runningConnections') : result?.integrationName ?? t('integrator.executionHistory')}</h2>
        <p>{t(globalRunning ? 'integrator.runningConnectionsHelp' : 'integrator.executionHistoryHelp')}</p></div>
        <label className="page-size-control"><span>{t('integrator.rowsPerPage')}</span>
          <select value={pageSize} onChange={event => {
            const next = Number(event.target.value) as RecordListPageSize
            rememberPageSize(next); setPageSize(next); setPage(1)
          }}>{recordListPageSizes.map(size => <option value={size} key={size}>{size}</option>)}</select>
        </label></div>
      <div className="administration-table-wrap"><table className="data-table administration-table">
        <thead><tr><SortableHeaders sort={tableSort} columns={[...(globalRunning?[["name",t('integrator.name')] as const]:[]),["kind",t('integrator.connectionType')],["status",t('planning.status')],["started",t('integrator.executionStarted')],["pages",t('integrator.testPages')],["records",t('integrator.testRecords')]]}/><th>{t('aplPanel.actions')}</th></tr></thead>
        <tbody>{tableSort.sortedRows.map(item => <tr key={item.id}>
          {globalRunning&&<td>{item.integrationName}</td>}
          <td><span className={`status-pill ${item.kind === 'Test' ? 'warning' : 'success'}`}>
            {t(`integrator.executionKind.${item.kind}`)}</span></td>
          <td><span className={`status-pill ${item.status === 'Succeeded' ? 'success' :
            item.status === 'Failed' ? 'danger' : 'warning'}`}>
            {t(`integrator.executionStatus.${item.status}`)}</span></td>
          <td>{formatDateTime(item.startedAtUtc ?? item.createdAtUtc)}</td>
          <td>{formatNumber(item.pagesRead)}</td><td>{formatNumber(item.recordsRead)}</td>
          <td><div className="table-actions"><button className="table-action" type="button"
            onClick={() => openDetail(item)}>{t('integrator.testDetails')}</button>
            {item.kind !== 'Test' && (item.status === 'Pending' || item.status === 'Running') &&
              <button className="table-action danger" type="button" disabled={busy}
                onClick={() => void cancelExecution(item)}>{t('integrator.cancelExecution')}</button>}
          </div></td>
        </tr>)}</tbody></table></div>
      {busy && !result && <p className="empty-state">{t('integrator.loading')}</p>}
      {!busy && result?.items.length === 0 && <p className="empty-state">
        {t(globalRunning ? 'integrator.runningConnectionsEmpty' : 'integrator.executionHistoryEmpty')}</p>}
      <footer className="table-pagination">
        <span>{t('integrator.pageOf').replace('{page}', formatNumber(page))
          .replace('{total}', formatNumber(totalPages))} · {t('integrator.totalConnections')}: {' '}
          {formatNumber(result?.totalCount ?? 0)}</span>
        <div className="record-list-pagination-actions"><button className="secondary-button" type="button" aria-label={t('integrator.previousPage')} disabled={busy || page <= 1}
          onClick={() => setPage(value => value - 1)}><span className="responsive-action-icon" aria-hidden="true">←</span><span className="responsive-action-label">{t('integrator.previousPage')}</span></button>
        <button className="secondary-button" type="button" disabled={busy || page >= totalPages}
          aria-label={t('integrator.nextPage')} onClick={() => setPage(value => value + 1)}><span className="responsive-action-label">{t('integrator.nextPage')}</span><span className="responsive-action-icon" aria-hidden="true">→</span></button></div>
      </footer>
    </section>

    {confirmationDialog}
    {selected && <AppModal title={t('integrator.testDetails')}
      closeLabel={t('nav.closeMenu')} onClose={() => setSelected(null)} wide>
      <div className="integration-test-detail">
        <div className="integrator-summary">
          <article><span>{t('planning.status')}</span><strong>{t(`integrator.executionStatus.${selected.status}`)}</strong></article>
          <article><span>{t('integrator.testHttpStatus')}</span><strong>{selected.httpStatus || '—'}</strong></article>
          <article><span>{t('integrator.testDuration')}</span><strong>{readableDuration(selected.elapsedMilliseconds, formatNumber, t)}</strong></article>
          <article><span>{t('integrator.testContentType')}</span><strong>{selected.responseContentType || '—'}</strong></article>
        </div>
        <dl className="record-detail-grid">
          <div><dt>{t('integrator.name')}</dt><dd>{selected.integrationName}</dd></div>
          <div><dt>{t('integrator.executionStarted')}</dt><dd>{formatDateTime(selected.startedAtUtc ?? selected.createdAtUtc)}</dd></div>
          <div><dt>{t('integrator.testPages')}</dt><dd>{formatNumber(selected.pagesRead)}</dd></div>
          <div><dt>{t('integrator.testRecords')}</dt><dd>{formatNumber(selected.recordsRead)}</dd></div>
          <div className="full-width"><dt>{t('integrator.testFinalUrl')}</dt><dd><code>{selected.finalRequestUrl || '—'}</code></dd></div>
          {selected.errorCode && <div className="full-width"><dt>{t('integrator.testResult')}</dt><dd className="form-error">{t(selected.errorCode as Parameters<typeof t>[0])}</dd></div>}
          {selected.errorDetail && <div className="full-width"><dt>{t('integrator.failureReason')}</dt><dd><code>{selected.errorDetail}</code></dd></div>}
        </dl>
        <section className="integration-timing-breakdown">
          <h3>{t('integrator.timingBreakdown')}</h3>
          <p className="form-help">{t('integrator.timingBreakdownHelp')}</p>
          {selected.elapsedMilliseconds > 0 && measuredMilliseconds === 0
            ? <p className="empty-state">{t('integrator.timingUnavailable')}</p>
            : <div className="integrator-summary">
              {timingRows.map(([label, milliseconds]) => <article key={label}>
                <span>{t(label)}</span>
                <strong>{readableDuration(milliseconds, formatNumber, t)}</strong>
              </article>)}
              <article><span>{t('integrator.timingOther')}</span>
                <strong>{readableDuration(otherMilliseconds, formatNumber, t)}</strong>
              </article>
            </div>}
        </section>
        <h3>{t('integrator.testResponseSample')}</h3>
        <p className="form-help">{t('integrator.testResponseSampleHelp')}</p>
        <pre className="integration-response-sample">{selected.responseSample || t('integrator.testNoSample')}</pre>
        <div className="form-actions">
          <button type="button" className="secondary-button" disabled={busy}
            onClick={() => void showDetail('json')}>{t(selected.direction === 'Outbound'
              ? 'integrator.viewRequestJson' : 'integrator.viewResponseJsonExample')}</button>
          <button type="button" className="secondary-button" disabled={busy}
            onClick={() => void showDetail('records')}>{t('integrator.viewExecutionDetail')}</button>
        </div>
        {detailView === 'json' && detail && <pre className="integration-response-sample">
          {detail.responsePayloadJson ? prettyResponseJson(detail.responsePayloadJson) : t('integrator.testNoSample')}</pre>}
        {detailView === 'records' && detail && (detail.items.length === 0
          ? <p className="empty-state">{t('integrator.executionNoErrors')}</p>
          : <div className="administration-table-wrap"><table className="data-table administration-table"><thead><tr><th>#</th><th>{t('integrator.externalKey')}</th><th>{t('planning.status')}</th><th>{t('integrator.failureReason')}</th></tr></thead><tbody>{detail.items.map(item => <tr key={item.sequence}><td>{item.sequence}</td><td><code>{item.externalKey || '—'}</code></td><td><span className="status-pill danger">{t('integrator.itemStatus.Failed')}</span></td><td>{integrationErrorReason(item.errorCode, item.errorDetail, t)}<details><summary>{t('integrator.viewRecordJson')}</summary><pre className="integration-response-sample">{item.recordJson}</pre></details></td></tr>)}</tbody></table></div>)}
      </div>
    </AppModal>}
  </main>
}
